/**
 * Desk widget directory.
 *
 * One registry is the chooser. Dashboard panels registered here are the
 * glance panels already on the desk (hero, gantt, board, tickets, today,
 * assigned). Hero-strip regions (fleet status, alert board) are a later
 * step: add a `hero-region` entry to WIDGET_REGISTRY and the chooser
 * follows. Do not keep a second list.
 *
 * A host is any surface that paints widgets from this registry: the
 * dashboard today, and later a side panel, a dashboard tab, or the hero
 * strip / board header. The document is a map of host id → slot boards.
 * This build only paints dashboard, schedule, and map. Reserved host ids
 * are stored and round-tripped; they are not given a default stack and
 * they are not drawn.
 *
 * Layout documents ride the company snapshot (Nanode Push / Pull) and are
 * normalized here so a missing save becomes today's stack.
 */

export const WIDGET_LAYOUT_VERSION = 1 as const;
export const WIDGET_COLUMNS = 12;
export const WIDGET_ROW_PX = 48;
/** Snap / place motion. Spec is 150–200ms ease-out. */
export const WIDGET_EASE_MS = 180;

/** Hosts this desk paints. A missing document seeds only these. */
export const PAINTED_WIDGET_HOSTS = ["dashboard", "schedule", "map"] as const;
export type PaintedWidgetHost = (typeof PAINTED_WIDGET_HOSTS)[number];

/**
 * Hosts a later layout will paint. Recognized so a save from that desk
 * survives a pull on this one. Not painted and not seeded here.
 */
export const RESERVED_WIDGET_HOSTS = ["side-panel", "dashboard-tab", "hero-strip", "board-header"] as const;

export const DASHBOARD_ROLES = ["dispatcher", "pilot"] as const;
export type DashboardWidgetRole = (typeof DASHBOARD_ROLES)[number];

/** `hero-region` is reserved for the later hero strip. None are registered yet. */
export type WidgetCategory = "dashboard-panel" | "hero-region";

export type WidgetDefinition = {
  id: string;
  title: string;
  summary: string;
  category: WidgetCategory;
  /**
   * Hosts that can place this widget. The chooser still lists the whole
   * registry on every host. Do not tag a future host until that host exists,
   * or the widget would become placeable the moment the host mounts.
   */
  hosts: readonly string[];
  minColSpan: number;
  maxColSpan: number;
  minRowSpan: number;
  maxRowSpan: number;
  defaultColSpan: number;
  defaultRowSpan: number;
};

/**
 * Glance panel ids. Desktop `GLANCE_PANELS` must stay equal to this list.
 * The hero strip is not one of these.
 */
export const DASHBOARD_PANEL_IDS = [
  "hero",
  "gantt",
  "board",
  "tickets",
  "today",
  "assigned",
  "links",
  "holds",
  "brief",
  "crew",
  "assist",
] as const;
export type DashboardPanelId = (typeof DASHBOARD_PANEL_IDS)[number];

const DASHBOARD_AND_SCHEDULE = ["dashboard", "schedule"] as const;

export const WIDGET_REGISTRY: readonly WidgetDefinition[] = [
  {
    id: "hero",
    title: "Hero",
    summary: "Glance hero. Gantt or the map card, whichever Desk → Hero is set to.",
    category: "dashboard-panel",
    hosts: ["dashboard"],
    minColSpan: 6,
    maxColSpan: 12,
    minRowSpan: 4,
    maxRowSpan: 24,
    defaultColSpan: 12,
    defaultRowSpan: 10,
  },
  {
    id: "gantt",
    title: "Gantt",
    summary: "Schedule board. On Dashboard this is the compact glance. On Schedule it is the full board.",
    category: "dashboard-panel",
    hosts: DASHBOARD_AND_SCHEDULE,
    minColSpan: 6,
    maxColSpan: 12,
    minRowSpan: 4,
    maxRowSpan: 32,
    defaultColSpan: 12,
    defaultRowSpan: 12,
  },
  {
    id: "board",
    title: "Needs action",
    summary: "Inbox, support, conflicts, and the pilot assign glance.",
    category: "dashboard-panel",
    hosts: ["dashboard"],
    minColSpan: 4,
    maxColSpan: 12,
    minRowSpan: 2,
    maxRowSpan: 16,
    defaultColSpan: 12,
    defaultRowSpan: 3,
  },
  {
    id: "tickets",
    title: "Tickets",
    summary: "Next, airborne, and accepted tickets.",
    category: "dashboard-panel",
    hosts: ["dashboard"],
    minColSpan: 4,
    maxColSpan: 12,
    minRowSpan: 2,
    maxRowSpan: 20,
    defaultColSpan: 12,
    defaultRowSpan: 5,
  },
  {
    id: "today",
    title: "Today",
    summary: "Live phase strip and the last log cue.",
    category: "dashboard-panel",
    hosts: ["dashboard"],
    minColSpan: 4,
    maxColSpan: 12,
    minRowSpan: 2,
    maxRowSpan: 12,
    defaultColSpan: 12,
    defaultRowSpan: 4,
  },
  {
    id: "assigned",
    title: "Assigned",
    summary: "Pilot home flight cards.",
    category: "dashboard-panel",
    hosts: ["dashboard"],
    minColSpan: 4,
    maxColSpan: 12,
    minRowSpan: 3,
    maxRowSpan: 24,
    defaultColSpan: 12,
    defaultRowSpan: 8,
  },
  {
    id: "links",
    title: "Quick links",
    summary: "Refresh, Inbox, Add ferry, Gantt, Board and the More shortcuts.",
    category: "dashboard-panel",
    hosts: ["dashboard"],
    minColSpan: 4,
    maxColSpan: 12,
    minRowSpan: 1,
    maxRowSpan: 4,
    defaultColSpan: 12,
    defaultRowSpan: 2,
  },
  {
    id: "holds",
    title: "Grounding holds",
    summary: "Cancelled tickets to reinstate and lines the sim would not back.",
    category: "dashboard-panel",
    hosts: ["dashboard"],
    minColSpan: 4,
    maxColSpan: 12,
    minRowSpan: 2,
    maxRowSpan: 16,
    defaultColSpan: 12,
    defaultRowSpan: 4,
  },
  {
    id: "brief",
    title: "Charter brief",
    summary: "Company charter ops brief.",
    category: "dashboard-panel",
    hosts: ["dashboard"],
    minColSpan: 4,
    maxColSpan: 12,
    minRowSpan: 3,
    maxRowSpan: 20,
    defaultColSpan: 12,
    defaultRowSpan: 6,
  },
  {
    id: "crew",
    title: "Crew",
    summary: "Company roster. Editing a member opens the right panel.",
    category: "dashboard-panel",
    hosts: ["dashboard"],
    minColSpan: 4,
    maxColSpan: 12,
    minRowSpan: 3,
    maxRowSpan: 20,
    defaultColSpan: 12,
    defaultRowSpan: 6,
  },
  {
    id: "assist",
    title: "Dispatch assist",
    summary: "Problems on the board, Propose plan and the company plan. The right panel Plan tab shows the same.",
    category: "dashboard-panel",
    hosts: ["dashboard"],
    minColSpan: 4,
    maxColSpan: 12,
    minRowSpan: 2,
    maxRowSpan: 16,
    defaultColSpan: 12,
    defaultRowSpan: 5,
  },
];

export type WidgetPlacement = {
  instanceId: string;
  widgetId: string;
  col: number;
  row: number;
  colSpan: number;
  rowSpan: number;
};

export type WidgetBoardLayout = {
  columns: number;
  placements: WidgetPlacement[];
};

/**
 * One host in the layout document.
 * `customized[slot]` is true after that slot is edited.
 * Dashboard slots are `dispatcher` and `pilot`. Every other host uses `shared`.
 * Extra slot keys are kept so a later role split is not wiped.
 */
export type WidgetHostRecord = {
  customized: Record<string, boolean>;
  boards: Record<string, WidgetBoardLayout>;
};

/**
 * Company-snapshot document. Companion can read this later; this build does not touch the PWA.
 * `hosts` is open: a host id this desk does not paint is preserved, including widget ids
 * that are not in this build's registry.
 */
export type WidgetLayouts = {
  version: typeof WIDGET_LAYOUT_VERSION;
  updatedAtUtc: string;
  hosts: Record<string, WidgetHostRecord>;
};

/** Rust file. Same document plus the desk checkbox so one command restores both. */
export type WidgetLayoutFile = WidgetLayouts & {
  editorEnabled: boolean;
};

export type WidgetChooserEntry = WidgetDefinition & {
  placed: boolean;
  placeable: boolean;
};

export function widgetById(id: string): WidgetDefinition | null {
  return WIDGET_REGISTRY.find((row) => row.id === id) ?? null;
}

/** Chooser rows. Always the registry, in registry order — never a second catalog. */
export function widgetsForChooser(hostId: string, board: WidgetBoardLayout): WidgetChooserEntry[] {
  const placed = new Set(board.placements.map((row) => row.widgetId));
  return WIDGET_REGISTRY.map((row) => ({
    ...row,
    placed: placed.has(row.id),
    placeable: row.hosts.includes(hostId) && !placed.has(row.id),
  }));
}

const HOST_KEY = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;

function isSafeKey(value: string): boolean {
  return HOST_KEY.test(value);
}

export function isPaintedWidgetHost(hostId: string): hostId is PaintedWidgetHost {
  return (PAINTED_WIDGET_HOSTS as readonly string[]).includes(hostId);
}

/**
 * Dashboard keeps a board per role so a dispatcher edit does not drop the
 * pilot stack. Every other host is one shared board.
 */
export function hostSlot(hostId: string, role: DashboardWidgetRole = "dispatcher"): string {
  return hostId === "dashboard" ? role : "shared";
}

export function defaultBoardForHost(hostId: string, slot: string): WidgetBoardLayout {
  if (hostId === "dashboard" && slot === "pilot") return defaultPilotDashboard();
  if (hostId === "dashboard") return defaultDispatcherDashboard();
  if (hostId === "schedule") return defaultScheduleBoard();
  return emptyBoard();
}

export function emptyBoard(): WidgetBoardLayout {
  return { columns: WIDGET_COLUMNS, placements: [] };
}

function stack(ids: readonly string[]): WidgetBoardLayout {
  const placements: WidgetPlacement[] = [];
  let row = 0;
  for (const id of ids) {
    const def = widgetById(id);
    if (!def) continue;
    placements.push({
      instanceId: def.id,
      widgetId: def.id,
      col: 0,
      row,
      colSpan: def.defaultColSpan,
      rowSpan: def.defaultRowSpan,
    });
    row += def.defaultRowSpan;
  }
  return { columns: WIDGET_COLUMNS, placements };
}

/**
 * Dispatcher home: quick links, needs, grounding holds, hero, tickets. Gantt stays inside the hero.
 * Charter brief and Crew open from Quick links until someone places them.
 */
export function defaultDispatcherDashboard(): WidgetBoardLayout {
  return stack(["links", "board", "assist", "holds", "hero", "tickets"]);
}

/** Pilot home: quick links, today, needs/assign glance, hero, tickets, assigned cards. */
export function defaultPilotDashboard(): WidgetBoardLayout {
  return stack(["links", "today", "board", "assist", "hero", "tickets", "assigned"]);
}

/** Schedule is the full Gantt until someone edits it. */
export function defaultScheduleBoard(): WidgetBoardLayout {
  return stack(["gantt"]);
}

/**
 * Map has no glance panels. The board stays empty until a map widget exists.
 * Do not invent one here.
 */
export function defaultMapBoard(): WidgetBoardLayout {
  return emptyBoard();
}

function hostRecord(hostId: string): WidgetHostRecord {
  if (hostId === "dashboard") {
    return {
      customized: { dispatcher: false, pilot: false },
      boards: {
        dispatcher: defaultDispatcherDashboard(),
        pilot: defaultPilotDashboard(),
      },
    };
  }
  if (hostId === "schedule") {
    return {
      customized: { shared: false },
      boards: { shared: defaultScheduleBoard() },
    };
  }
  return {
    customized: { shared: false },
    boards: { shared: emptyBoard() },
  };
}

export function defaultWidgetLayouts(updatedAtUtc = ""): WidgetLayouts {
  return {
    version: WIDGET_LAYOUT_VERSION,
    updatedAtUtc,
    hosts: {
      dashboard: hostRecord("dashboard"),
      schedule: hostRecord("schedule"),
      map: hostRecord("map"),
    },
  };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function asInt(value: unknown, fallback: number, min: number, max: number): number {
  const n = typeof value === "number" ? value : typeof value === "string" && value.trim() ? Number(value) : NaN;
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, Math.trunc(n)));
}

function overlaps(a: WidgetPlacement, b: WidgetPlacement): boolean {
  return a.col < b.col + b.colSpan && b.col < a.col + a.colSpan && a.row < b.row + b.rowSpan && b.row < a.row + a.rowSpan;
}

function clampPlacement(raw: unknown, seen: Set<string>): WidgetPlacement | null {
  const row = asRecord(raw);
  if (!row) return null;
  const widgetId = typeof row.widgetId === "string" ? row.widgetId.trim() : "";
  const def = widgetById(widgetId);
  if (!def) return null;
  if (seen.has(widgetId)) return null;
  seen.add(widgetId);
  const colSpan = asInt(row.colSpan, def.defaultColSpan, def.minColSpan, def.maxColSpan);
  const rowSpan = asInt(row.rowSpan, def.defaultRowSpan, def.minRowSpan, def.maxRowSpan);
  const col = asInt(row.col, 0, 0, Math.max(0, WIDGET_COLUMNS - colSpan));
  const rowIndex = asInt(row.row, 0, 0, 400);
  const instanceId = typeof row.instanceId === "string" && row.instanceId.trim() ? row.instanceId.trim() : widgetId;
  return { instanceId, widgetId, col, row: rowIndex, colSpan, rowSpan };
}

function fits(placements: readonly WidgetPlacement[], candidate: WidgetPlacement, ignoreId?: string): boolean {
  if (candidate.col < 0 || candidate.row < 0) return false;
  if (candidate.colSpan < 1 || candidate.rowSpan < 1) return false;
  if (candidate.col + candidate.colSpan > WIDGET_COLUMNS) return false;
  const def = widgetById(candidate.widgetId);
  if (!def) return false;
  if (candidate.colSpan < def.minColSpan || candidate.colSpan > def.maxColSpan) return false;
  if (candidate.rowSpan < def.minRowSpan || candidate.rowSpan > def.maxRowSpan) return false;
  return placements.every((row) => row.instanceId === ignoreId || row.widgetId === ignoreId || !overlaps(row, candidate));
}

/** Pull each widget to the top-most row that keeps its column. No overlap, no empty row between widgets. */
export function compactBoard(board: WidgetBoardLayout): WidgetBoardLayout {
  const sorted = [...board.placements].sort((a, b) => a.row - b.row || a.col - b.col || a.widgetId.localeCompare(b.widgetId));
  const placed: WidgetPlacement[] = [];
  for (const row of sorted) {
    const def = widgetById(row.widgetId);
    if (!def) continue;
    let nextRow = 0;
    let guard = 0;
    while (guard < 500 && !fits(placed, { ...row, row: nextRow })) {
      nextRow += 1;
      guard += 1;
    }
    placed.push({ ...row, row: nextRow });
  }
  return { columns: WIDGET_COLUMNS, placements: placed };
}

function hydrateBoard(raw: unknown, fallback: WidgetBoardLayout): WidgetBoardLayout {
  const rec = asRecord(raw);
  if (!rec || !Array.isArray(rec.placements)) return fallback;
  const seen = new Set<string>();
  const placements: WidgetPlacement[] = [];
  for (const row of rec.placements) {
    const next = clampPlacement(row, seen);
    if (next) placements.push(next);
  }
  return compactBoard({ columns: WIDGET_COLUMNS, placements });
}

/** Geometry only. Unknown widget ids stay — used for hosts this desk does not paint. */
function hydrateOpaqueBoard(raw: unknown): WidgetBoardLayout {
  const rec = asRecord(raw);
  if (!rec || !Array.isArray(rec.placements)) return emptyBoard();
  const seen = new Set<string>();
  const placements: WidgetPlacement[] = [];
  for (const row of rec.placements) {
    const item = asRecord(row);
    if (!item) continue;
    const widgetId = typeof item.widgetId === "string" ? item.widgetId.trim() : "";
    if (!widgetId || widgetId.length > 64 || /\s/.test(widgetId) || seen.has(widgetId)) continue;
    seen.add(widgetId);
    const colSpan = asInt(item.colSpan, 12, 1, WIDGET_COLUMNS);
    const rowSpan = asInt(item.rowSpan, 1, 1, 48);
    const col = asInt(item.col, 0, 0, Math.max(0, WIDGET_COLUMNS - colSpan));
    const rowIndex = asInt(item.row, 0, 0, 400);
    const instanceRaw = typeof item.instanceId === "string" ? item.instanceId.trim() : "";
    const instanceId = instanceRaw && instanceRaw.length <= 64 ? instanceRaw : widgetId;
    placements.push({ instanceId, widgetId, col, row: rowIndex, colSpan, rowSpan });
  }
  return { columns: WIDGET_COLUMNS, placements };
}

function hydrateHost(hostId: string, raw: unknown): WidgetHostRecord | null {
  if (!isSafeKey(hostId)) return null;
  const painted = isPaintedWidgetHost(hostId);
  const rec = asRecord(raw);
  if (!rec && !painted) return null;
  const fallback = hostRecord(painted ? hostId : "");
  const boardsRaw = asRecord(rec?.boards);
  const flagsRaw = asRecord(rec?.customized);
  const slots = new Set<string>();
  if (painted) {
    for (const key of Object.keys(fallback.boards)) slots.add(key);
  }
  if (boardsRaw) {
    for (const key of Object.keys(boardsRaw)) {
      if (isSafeKey(key)) slots.add(key);
    }
  }
  if (flagsRaw) {
    for (const key of Object.keys(flagsRaw)) {
      if (isSafeKey(key)) slots.add(key);
    }
  }
  const boards: Record<string, WidgetBoardLayout> = {};
  const customized: Record<string, boolean> = {};
  for (const slot of slots) {
    customized[slot] = flagsRaw?.[slot] === true;
    const rawBoard = boardsRaw?.[slot];
    if (painted) {
      const fb = fallback.boards[slot] ?? emptyBoard();
      boards[slot] = hydrateBoard(rawBoard, customized[slot] ? emptyBoard() : fb);
    } else {
      boards[slot] = hydrateOpaqueBoard(rawBoard);
    }
  }
  if (!painted && !Object.keys(boards).length && !Object.keys(customized).length) return null;
  return { customized, boards };
}

export function hydrateWidgetLayouts(raw: unknown): WidgetLayouts {
  const fallback = defaultWidgetLayouts();
  const rec = asRecord(raw);
  if (!rec) return fallback;
  const hostsRaw = asRecord(rec.hosts);
  const hosts: Record<string, WidgetHostRecord> = {};
  for (const id of PAINTED_WIDGET_HOSTS) {
    hosts[id] = hydrateHost(id, hostsRaw?.[id]) ?? hostRecord(id);
  }
  if (hostsRaw) {
    for (const id of Object.keys(hostsRaw)) {
      if (isPaintedWidgetHost(id)) continue;
      const next = hydrateHost(id, hostsRaw[id]);
      if (next) hosts[id] = next;
    }
  }
  return {
    version: WIDGET_LAYOUT_VERSION,
    updatedAtUtc: typeof rec.updatedAtUtc === "string" ? rec.updatedAtUtc : "",
    hosts,
  };
}

export function boardFor(layouts: WidgetLayouts, hostId: string, role: DashboardWidgetRole = "dispatcher"): WidgetBoardLayout {
  const slot = hostSlot(hostId, role);
  return layouts.hosts[hostId]?.boards[slot] ?? defaultBoardForHost(hostId, slot);
}

export function isHostCustomized(layouts: WidgetLayouts, hostId: string, role: DashboardWidgetRole = "dispatcher"): boolean {
  const slot = hostSlot(hostId, role);
  return layouts.hosts[hostId]?.customized[slot] === true;
}

/**
 * What the view paints. An untouched host uses the role default so a
 * half-saved board cannot drop today's panels. A host this desk does not
 * paint has an empty default — do not copy the dashboard into it.
 */
export function resolvedBoard(
  layouts: WidgetLayouts,
  hostId: string,
  role: DashboardWidgetRole = "dispatcher",
): WidgetBoardLayout {
  const slot = hostSlot(hostId, role);
  if (!isHostCustomized(layouts, hostId, role)) return defaultBoardForHost(hostId, slot);
  return boardFor(layouts, hostId, role);
}

export function withHostBoard(
  layouts: WidgetLayouts,
  hostId: string,
  role: DashboardWidgetRole,
  board: WidgetBoardLayout,
  updatedAtUtc: string,
): WidgetLayouts {
  if (!isSafeKey(hostId)) return layouts;
  const next = hydrateWidgetLayouts(layouts);
  const slot = hostSlot(hostId, role);
  const host = next.hosts[hostId] ?? { customized: {}, boards: {} };
  host.boards[slot] = compactBoard(board);
  host.customized[slot] = true;
  next.hosts[hostId] = host;
  next.updatedAtUtc = updatedAtUtc;
  return next;
}

function firstFit(board: WidgetBoardLayout, widgetId: string, prefer?: { col: number; row: number }): WidgetPlacement | null {
  const def = widgetById(widgetId);
  if (!def) return null;
  if (!def.hosts.length) return null;
  const colSpan = def.defaultColSpan;
  const rowSpan = def.defaultRowSpan;
  const seeds: Array<{ col: number; row: number }> = [];
  if (prefer) {
    const col = Math.max(0, Math.min(WIDGET_COLUMNS - colSpan, prefer.col));
    seeds.push({ col, row: Math.max(0, prefer.row) });
  }
  seeds.push({ col: 0, row: 0 });
  for (const seed of seeds) {
    for (let row = seed.row; row < seed.row + 80; row += 1) {
      const candidate: WidgetPlacement = {
        instanceId: widgetId,
        widgetId,
        col: seed.col,
        row,
        colSpan,
        rowSpan,
      };
      if (fits(board.placements, candidate)) return candidate;
    }
  }
  return null;
}

export function addWidget(
  board: WidgetBoardLayout,
  widgetId: string,
  drop?: { col: number; row: number },
): { board: WidgetBoardLayout; placed: boolean } {
  if (!widgetById(widgetId)) return { board, placed: false };
  if (board.placements.some((row) => row.widgetId === widgetId)) return { board, placed: false };
  const spot = firstFit(board, widgetId, drop);
  if (!spot) return { board, placed: false };
  return {
    board: compactBoard({ columns: WIDGET_COLUMNS, placements: [...board.placements, spot] }),
    placed: true,
  };
}

export function deleteWidget(board: WidgetBoardLayout, widgetId: string): WidgetBoardLayout {
  return compactBoard({
    columns: WIDGET_COLUMNS,
    placements: board.placements.filter((row) => row.widgetId !== widgetId && row.instanceId !== widgetId),
  });
}

export function moveWidget(
  board: WidgetBoardLayout,
  widgetId: string,
  col: number,
  row: number,
): WidgetBoardLayout {
  const current = board.placements.find((item) => item.widgetId === widgetId);
  if (!current) return board;
  const nextCol = Math.max(0, Math.min(WIDGET_COLUMNS - current.colSpan, Math.trunc(col)));
  const nextRow = Math.max(0, Math.trunc(row));
  const candidate: WidgetPlacement = { ...current, col: nextCol, row: nextRow };
  const others = board.placements.filter((item) => item.widgetId !== widgetId);
  if (fits(others, candidate)) {
    return compactBoard({ columns: WIDGET_COLUMNS, placements: [...others, candidate] });
  }
  // The drop cell is taken. Shift that row downward so the widget can land
  // between its neighbors, then pack holes shut.
  const shifted = others.map((item) =>
    item.row >= nextRow ? { ...item, row: item.row + current.rowSpan } : item,
  );
  if (!fits(shifted, candidate)) return board;
  return compactBoard({ columns: WIDGET_COLUMNS, placements: [...shifted, candidate] });
}

export function resizeWidget(
  board: WidgetBoardLayout,
  widgetId: string,
  patch: { col?: number; row?: number; colSpan?: number; rowSpan?: number },
): WidgetBoardLayout {
  const current = board.placements.find((item) => item.widgetId === widgetId);
  const def = current ? widgetById(current.widgetId) : null;
  if (!current || !def) return board;
  const colSpan = asInt(patch.colSpan ?? current.colSpan, current.colSpan, def.minColSpan, def.maxColSpan);
  const rowSpan = asInt(patch.rowSpan ?? current.rowSpan, current.rowSpan, def.minRowSpan, def.maxRowSpan);
  const col = asInt(patch.col ?? current.col, current.col, 0, Math.max(0, WIDGET_COLUMNS - colSpan));
  const row = asInt(patch.row ?? current.row, current.row, 0, 400);
  const candidate: WidgetPlacement = { ...current, col, row, colSpan, rowSpan };
  const others = board.placements.filter((item) => item.widgetId !== widgetId);
  if (!fits(others, candidate)) return board;
  return compactBoard({ columns: WIDGET_COLUMNS, placements: [...others, candidate] });
}

export function boardWidgetIds(board: WidgetBoardLayout): string[] {
  return [...board.placements].sort((a, b) => a.row - b.row || a.col - b.col).map((row) => row.widgetId);
}

/** True when a full row between 0 and the last occupied row has no cell. */
export function boardHasRowGap(board: WidgetBoardLayout): boolean {
  if (!board.placements.length) return false;
  const max = Math.max(...board.placements.map((row) => row.row + row.rowSpan));
  for (let row = 0; row < max; row += 1) {
    const hit = board.placements.some((item) => row >= item.row && row < item.row + item.rowSpan);
    if (!hit) return true;
  }
  return false;
}

export function boardHasOverlap(board: WidgetBoardLayout): boolean {
  for (let i = 0; i < board.placements.length; i += 1) {
    for (let j = i + 1; j < board.placements.length; j += 1) {
      if (overlaps(board.placements[i]!, board.placements[j]!)) return true;
    }
  }
  return false;
}

export function boardHasOrphan(board: WidgetBoardLayout): boolean {
  const seen = new Set<string>();
  for (const row of board.placements) {
    if (!widgetById(row.widgetId)) return true;
    if (seen.has(row.widgetId)) return true;
    seen.add(row.widgetId);
  }
  return false;
}

/**
 * Schedule-hash fragment. Null when the editor is off and no host slot was
 * edited, so an older snapshot keeps today's hash.
 * Board keys are `${hostId}:${slot}`, host ids then slots, both sorted.
 * That order is part of the Nanode lockstep — keep the server copy identical.
 */
export function widgetLayoutScheduleBits(company: unknown): { widgetEditorEnabled?: true; widgetBoards?: Record<string, unknown> } | null {
  const rec = asRecord(company);
  if (!rec) return null;
  const behavior = asRecord(rec.deskBehavior);
  const editor = behavior?.widgetEditorEnabled === true;
  const layouts = asRecord(rec.widgetLayouts);
  const boards = layouts ? customizedBoardBits(layouts) : null;
  if (!editor && !boards) return null;
  const out: { widgetEditorEnabled?: true; widgetBoards?: Record<string, unknown> } = {};
  if (editor) out.widgetEditorEnabled = true;
  if (boards) out.widgetBoards = boards;
  return out;
}

function placementBits(board: unknown): Array<Record<string, number | string>> | null {
  const rec = asRecord(board);
  if (!rec || !Array.isArray(rec.placements)) return null;
  const rows = rec.placements
    .map((row) => asRecord(row))
    .filter((row): row is Record<string, unknown> => Boolean(row))
    .map((row) => ({
      widgetId: typeof row.widgetId === "string" ? row.widgetId : "",
      col: typeof row.col === "number" ? row.col : 0,
      row: typeof row.row === "number" ? row.row : 0,
      colSpan: typeof row.colSpan === "number" ? row.colSpan : 0,
      rowSpan: typeof row.rowSpan === "number" ? row.rowSpan : 0,
    }))
    .filter((row) => row.widgetId)
    .sort((a, b) => (a.row as number) - (b.row as number) || (a.col as number) - (b.col as number) || String(a.widgetId).localeCompare(String(b.widgetId)));
  return rows;
}

function customizedBoardBits(layouts: Record<string, unknown>): Record<string, unknown> | null {
  const hosts = asRecord(layouts.hosts);
  if (!hosts) return null;
  const out: Record<string, unknown> = {};
  for (const hostId of Object.keys(hosts).sort()) {
    const host = asRecord(hosts[hostId]);
    if (!host) continue;
    const flags = asRecord(host.customized);
    const boards = asRecord(host.boards);
    if (!flags || !boards) continue;
    const slots = Object.keys(flags)
      .filter((slot) => flags[slot] === true)
      .sort();
    for (const slot of slots) {
      const bits = placementBits(boards[slot]);
      if (bits) out[`${hostId}:${slot}`] = bits;
    }
  }
  return Object.keys(out).length ? out : null;
}

export function widgetLayoutFileFrom(layouts: WidgetLayouts, editorEnabled: boolean): WidgetLayoutFile {
  return { ...hydrateWidgetLayouts(layouts), editorEnabled: editorEnabled === true };
}

export function readWidgetLayoutFile(raw: unknown): { layouts: WidgetLayouts; editorEnabled: boolean } | null {
  const rec = asRecord(raw);
  if (!rec) return null;
  if (rec.version !== WIDGET_LAYOUT_VERSION) return null;
  return {
    layouts: hydrateWidgetLayouts(rec),
    editorEnabled: rec.editorEnabled === true,
  };
}
